// AdminComplianceScorecardPage.js — agent × archetype playbook hit-rate.
// Master-admin only. Drills down to individual calls where an agent
// wandered off the playbook. Runs off cached `playbook_coach_reports`
// so no new LLM cost.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Target, Loader2, RefreshCw, AlertCircle, ChevronRight, ChevronDown,
  ExternalLink, TrendingDown, User, Mail, Send, X, Settings2, BookOpen, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _pct = (rate) => rate == null ? '—' : `${Math.round(rate * 100)}%`;

const _rateBucket = (rate) => {
  if (rate == null) return 'bg-slate-500/10 text-slate-300 border-slate-500/30';
  if (rate >= 0.8) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
  if (rate >= 0.6) return 'bg-teal-500/20 text-teal-300 border-teal-500/40';
  if (rate >= 0.4) return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
  return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
};


// Manager Coach Assist — one-click "Book coaching on top-missed step"
// with a small confirm popover that previews the pre-filled record.
const BookCoachingButton = ({ domainId, agentId, archetype, days, headers, disabled }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(false);

  const loadPreview = async (e) => {
    e.stopPropagation();
    if (disabled || open) { setOpen(false); return; }
    setOpen(true); setErr(''); setPreview(null); setBooked(false); setBusy(true);
    try {
      const r = await axios.get(
        `${API}/admin/compliance-scorecard/coaching-preview?domain_id=${domainId}&agent_id=${encodeURIComponent(agentId)}&archetype=${archetype}&days=${days}`,
        { headers },
      );
      setPreview(r.data);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Preview failed.'); }
    finally { setBusy(false); }
  };

  const confirmBook = async (e) => {
    e.stopPropagation();
    setBusy(true); setErr('');
    try {
      await axios.post(
        `${API}/admin/compliance-scorecard/book-coaching`,
        { domain_id: domainId, agent_id: agentId, archetype, days },
        { headers },
      );
      setBooked(true);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Booking failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={loadPreview}
        disabled={disabled}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors ${
          disabled
            ? 'bg-white/[0.02] border-white/5 text-white/30 cursor-not-allowed'
            : 'bg-indigo-500/15 border-indigo-500/30 text-indigo-200 hover:bg-indigo-500/25'
        }`}
        title={disabled ? 'No top-missed step to coach on yet' : 'Book coaching on top-missed step'}
        data-testid={`book-coaching-${agentId}-${archetype}`}
      >
        <BookOpen className="w-3 h-3" />Book coaching
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 w-[380px] bg-[#0F172A] border border-white/10 rounded-lg shadow-2xl p-3 text-xs"
          onClick={(e) => e.stopPropagation()}
          data-testid={`book-coaching-popover-${agentId}-${archetype}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 font-semibold text-[11px] uppercase tracking-widest">Book coaching</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="text-white/50 hover:text-white/90"
              data-testid={`book-coaching-close-${agentId}-${archetype}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {busy && !preview && !err && (
            <div className="text-white/50 flex items-center gap-2 py-2"><Loader2 className="w-3 h-3 animate-spin" />Loading preview…</div>
          )}
          {err && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 text-rose-200 flex items-start gap-1.5">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />{err}
            </div>
          )}
          {preview && !booked && (
            <div className="space-y-1.5 text-white/80">
              <div><span className="text-white/50">Consultant:</span> <b>{preview.consultantName || preview.consultantId}</b></div>
              <div><span className="text-white/50">Focus:</span> <b className="text-teal-300">{preview.focusCriterion.replace(/_/g, ' ')}</b></div>
              <div className="bg-white/5 border border-white/10 rounded px-2 py-1.5 italic text-white/90">
                &ldquo;{preview.focusBehaviour}&rdquo;
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px] text-white/60">
                <div>Baseline<br /><b className="text-white/90 text-sm">{preview.baselineScore}%</b></div>
                <div>Sample<br /><b className="text-white/90 text-sm">{preview.sample_size}</b></div>
                <div>Missed<br /><b className="text-white/90 text-sm">{Math.round((preview.miss_pct || 0) * 100)}%</b></div>
              </div>
              <div className="text-white/50">Evidence: <span className="text-white/80">{preview.evidenceCalls.length} worst call{preview.evidenceCalls.length === 1 ? '' : 's'}</span></div>
              <button
                type="button"
                onClick={confirmBook}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 mt-1 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold text-xs"
                data-testid={`book-coaching-confirm-${agentId}-${archetype}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookOpen className="w-3 h-3" />}
                Book coaching record
              </button>
            </div>
          )}
          {booked && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-2 text-emerald-200 flex items-start gap-1.5" data-testid={`book-coaching-success-${agentId}-${archetype}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                Coaching record booked. The consultant will see it in their <b>My Coaching</b> tab.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const DrillDown = ({ domainId, agentId, archetype, days, headers, onClose }) => {
  const [calls, setCalls] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/compliance-scorecard/drill-down?domain_id=${domainId}&agent_id=${encodeURIComponent(agentId)}&archetype=${archetype}&days=${days}`,
          { headers },
        );
        setCalls(r.data.calls || []);
      } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load drill-down.'); }
    })();
  }, [domainId, agentId, archetype, days, headers]);

  if (err) return <div className="text-rose-300 text-xs px-3 py-2">{err}</div>;
  if (!calls) return <div className="text-white/50 text-xs px-3 py-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>;
  if (!calls.length) return <div className="text-white/50 text-xs px-3 py-2 italic">No graded calls in this cell yet.</div>;

  return (
    <div className="bg-black/20 rounded-lg p-2 space-y-1.5" data-testid="drill-down">
      {calls.map((c) => (
        <div key={c.call_id} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 flex items-start gap-3 text-xs">
          <div className={`text-sm font-bold shrink-0 min-w-[45px] text-center px-1.5 py-0.5 rounded ${_rateBucket(c.hit_rate)}`}>
            {_pct(c.hit_rate)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white/80 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-white/50">{c.call_id.slice(0, 24)}…</span>
              <span className="text-white/50">·</span>
              <span>{c.customer_phone || '—'}</span>
              <span className="text-white/40">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
            </div>
            {c.steps_missed?.length > 0 && (
              <div className="mt-1 text-rose-300/80 text-[11px]">
                <span className="text-white/50">Missed:</span> {c.steps_missed.map((s) => s.step_summary).join(' · ')}
              </div>
            )}
            {c.next_call_focus && (
              <div className="mt-1 text-teal-300/80 text-[11px]">
                <span className="text-white/50">Coach:</span> {c.next_call_focus}
              </div>
            )}
          </div>
          <Link
            to={`/calls/${c.call_id}`}
            className="text-teal-300 hover:text-teal-200 text-[11px] flex items-center gap-1 shrink-0 mt-0.5"
            onClick={() => onClose && onClose()}
          >
            Open <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      ))}
    </div>
  );
};

const AdminComplianceScorecardPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  // Digest preview / send-now modal
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestHtml, setDigestHtml] = useState('');
  const [digestMeta, setDigestMeta] = useState(null);
  const [digestErr, setDigestErr] = useState('');
  const [digestBusy, setDigestBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Per-tenant hit-rate ceiling
  const [ceiling, setCeiling] = useState(0.75);
  const [ceilingBusy, setCeilingBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`, { headers });
        const rows = Array.isArray(r.data) ? r.data : (r.data.domains || []);
        setDomains(rows);
        const tf = rows.find((d) => (d.name || '').toLowerCase().includes('total fusion') || (d.yabbit_domain || '').toLowerCase().includes('totalfusion'));
        if (tf) setDomainId(tf.id); else if (rows[0]) setDomainId(rows[0].id);
      } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load domains.'); }
    })();
  }, [headers]);

  const load = useCallback(async () => {
    if (!domainId) return;
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/admin/compliance-scorecard?domain_id=${domainId}&days=${days}`, { headers });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load scorecard.');
      setData(null);
    } finally { setLoading(false); }
  }, [domainId, days, headers]);

  useEffect(() => { if (domainId) load(); }, [domainId, days, load]);

  // Load current per-domain ceiling from the domains list
  useEffect(() => {
    if (!domainId) return;
    const dom = domains.find((d) => d.id === domainId);
    if (dom?.compliance_hit_rate_ceiling != null) setCeiling(Number(dom.compliance_hit_rate_ceiling));
    else setCeiling(0.75);
  }, [domainId, domains]);

  const saveCeiling = useCallback(async () => {
    if (!domainId) return;
    setCeilingBusy(true);
    try {
      await axios.patch(`${API}/admin/domains/${domainId}/compliance-threshold`, { ceiling }, { headers });
      const r = await axios.get(`${API}/admin/domains`, { headers });
      setDomains(Array.isArray(r.data) ? r.data : (r.data.domains || []));
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to save ceiling.'); }
    finally { setCeilingBusy(false); }
  }, [domainId, ceiling, headers]);

  const openDigestPreview = useCallback(async () => {
    if (!domainId) return;
    setDigestOpen(true); setDigestErr(''); setDigestHtml(''); setDigestMeta(null); setSendResult(null); setDigestBusy(true);
    try {
      const r = await axios.get(`${API}/admin/compliance-digest/preview?domain_id=${domainId}&days=7`, { headers });
      if (r.data?.has_content) {
        setDigestHtml(r.data.html);
        setDigestMeta({ tenant: r.data.tenant_name, cells: (r.data.cells || []).length, reports: r.data.total_reports });
      } else {
        setDigestErr(`No digest to send — ${r.data?.reason || 'no coaching data yet'}.`);
      }
    } catch (e) { setDigestErr(e?.response?.data?.detail || 'Preview failed.'); }
    finally { setDigestBusy(false); }
  }, [domainId, headers]);

  const sendDigestNow = useCallback(async () => {
    if (!domainId) return;
    setDigestBusy(true); setSendResult(null); setDigestErr('');
    try {
      const r = await axios.post(`${API}/admin/compliance-digest/send-now`, { domain_id: domainId, days: 7 }, { headers });
      setSendResult(r.data);
    } catch (e) { setDigestErr(e?.response?.data?.detail || 'Send failed.'); }
    finally { setDigestBusy(false); }
  }, [domainId, headers]);

  const rows = data?.scorecard || [];

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="compliance-scorecard-page">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-teal-400" />
              Playbook Compliance Scorecard
            </h1>
            <p className="text-sm text-white/60 mt-1 max-w-3xl">
              For each agent × archetype, what percentage of the drafted playbook steps did they hit? Worst compliance surfaces first. Click any row to drill into the exact calls where they wandered off.
            </p>
          </div>
          <button
            onClick={openDigestPreview}
            disabled={!domainId}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold rounded-lg"
            data-testid="compliance-preview-digest"
          >
            <Mail className="w-3.5 h-3.5" />Preview & Send Digest
          </button>
          <button
            onClick={load}
            disabled={loading || !domainId}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50"
            data-testid="compliance-reload"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reload
          </button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 flex items-center flex-wrap gap-3">
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold">Domain</label>
          <select
            value={domainId}
            onChange={(e) => setDomainId(e.target.value)}
            className="bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1 min-w-[200px]"
            data-testid="compliance-domain-picker"
          >
            <option value="">— select —</option>
            {domains.map((d) => (<option key={d.id} value={d.id}>{d.name || d.yabbit_domain}</option>))}
          </select>
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold ml-3">Window</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#0A0F1C] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-32"
          >
            {[7, 14, 30, 60, 90].map((n) => <option key={n} value={n}>{n} days</option>)}
          </select>
          {data && (
            <div className="text-white/50 text-xs ml-auto">
              {data.total_reports} calls coached · {data.agents_count} agents · {rows.length} rows
            </div>
          )}
        </div>

        {/* Threshold ceiling slider */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="compliance-threshold-panel">
          <Settings2 className="w-4 h-4 text-teal-400 shrink-0" />
          <label className="text-white/60 text-xs uppercase tracking-wider font-semibold">Digest ceiling</label>
          <input
            type="range"
            min="0.1" max="0.99" step="0.05"
            value={ceiling}
            onChange={(e) => setCeiling(Number(e.target.value))}
            className="flex-1 min-w-[200px] max-w-md"
            data-testid="compliance-ceiling-slider"
          />
          <span className="text-teal-300 font-bold min-w-[45px] text-right">{Math.round(ceiling * 100)}%</span>
          <span className="text-white/50 text-xs flex-1 min-w-[150px]">Cells at or below this hit rate get surfaced in the weekly digest.</span>
          <button
            onClick={saveCeiling}
            disabled={ceilingBusy || !domainId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold rounded"
            data-testid="compliance-ceiling-save"
          >
            {ceilingBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </button>
        </div>

        {err && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{err}
          </div>
        )}

        {loading && !data ? (
          <div className="text-center py-12 text-white/50"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />Loading…</div>
        ) : rows.length === 0 && data ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 text-center text-white/50">
            <TrendingDown className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-white/80 font-semibold">No playbook coach reports in this window yet</div>
            <div className="text-white/50 text-sm mt-1 max-w-lg mx-auto">
              As shadow-graded calls come in with classified archetypes and transcripts, the Playbook Coach auto-runs and this scorecard fills up. Widen the window or check back tomorrow.
            </div>
          </div>
        ) : (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/30 text-white/50 text-[10px] uppercase tracking-widest">
                  <th className="text-left px-3 py-2 font-semibold">Agent</th>
                  <th className="text-left px-3 py-2 font-semibold">Archetype</th>
                  <th className="text-center px-3 py-2 font-semibold">Calls</th>
                  <th className="text-center px-3 py-2 font-semibold">Hit rate</th>
                  <th className="text-left px-3 py-2 font-semibold">Top missed step</th>
                  <th className="text-right px-3 py-2 font-semibold w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const key = `${r.agent_id}::${r.archetype}`;
                  const expanded = expandedKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => setExpandedKey(expanded ? null : key)}
                        data-testid={`compliance-row-${key}`}
                      >
                        <td className="px-3 py-2.5 text-white/90 flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-white/40" />{r.agent_name}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-mono text-xs text-teal-300 bg-teal-500/10 border border-teal-500/20 px-2 py-0.5 rounded">{r.archetype}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-white/70 font-mono">{r.sample_size}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-bold ${_rateBucket(r.avg_hit_rate)}`}>
                            {_pct(r.avg_hit_rate)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-white/60 text-xs max-w-md">
                          <div className="flex items-start justify-between gap-2">
                            <div className="truncate flex-1">
                              {r.top_missed_step
                                ? (<><span className="text-rose-300">{r.top_missed_step.step_summary}</span> <span className="text-white/40">({_pct(r.top_missed_step.miss_pct)} of calls)</span></>)
                                : <span className="text-white/30 italic">no consistent misses</span>}
                            </div>
                            <BookCoachingButton
                              domainId={domainId}
                              agentId={r.agent_id}
                              archetype={r.archetype}
                              days={days}
                              headers={headers}
                              disabled={!r.top_missed_step}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-white/40">
                          {expanded ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} className="px-3 pb-3 pt-1 bg-black/20">
                            <DrillDown
                              domainId={domainId}
                              agentId={r.agent_id}
                              archetype={r.archetype}
                              days={days}
                              headers={headers}
                              onClose={() => setExpandedKey(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Digest Preview Modal */}
        {digestOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => !digestBusy && setDigestOpen(false)}
            data-testid="digest-modal"
          >
            <div
              className="bg-[#0F172A] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-teal-400" />Compliance digest preview
                </h3>
                <button
                  onClick={() => setDigestOpen(false)}
                  className="text-white/60 hover:text-white p-1"
                  data-testid="digest-modal-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {digestBusy && !digestHtml && !digestErr && (
                  <div className="text-center py-12 text-white/50">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />Loading preview…
                  </div>
                )}
                {digestErr && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-200 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{digestErr}
                  </div>
                )}
                {sendResult && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-200 mb-3" data-testid="digest-send-result">
                    {sendResult.sent
                      ? `Sent to ${(sendResult.recipients || []).length} supervisor(s): ${(sendResult.recipients || []).join(', ')}`
                      : `Skipped — ${sendResult.skipped || 'no reason'}`}
                  </div>
                )}
                {digestHtml && (
                  <>
                    {digestMeta && (
                      <div className="text-white/60 text-xs mb-2">
                        {digestMeta.tenant} · {digestMeta.cells} problem cell{digestMeta.cells === 1 ? '' : 's'} · {digestMeta.reports} calls coached · last 7 days
                      </div>
                    )}
                    <iframe
                      title="Digest preview"
                      srcDoc={digestHtml}
                      className="w-full bg-white rounded-lg border border-white/10"
                      style={{ height: '55vh' }}
                      data-testid="digest-preview-iframe"
                    />
                  </>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 shrink-0">
                <button
                  onClick={() => setDigestOpen(false)}
                  disabled={digestBusy}
                  className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  onClick={sendDigestNow}
                  disabled={digestBusy || !digestHtml || !!sendResult?.sent}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white font-semibold rounded-lg"
                  data-testid="digest-send-now"
                >
                  {digestBusy && !sendResult ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {sendResult?.sent ? 'Sent ✓' : 'Send now'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminComplianceScorecardPage;
